export default {
  duplicate_name: '"{value}" already exists. Please choose a different name.',
  // The name belongs to an archived entry (docs/archiving.md), which is restored instead.
  duplicate_name_archived: '"{value}" belongs to an archived entry. Restore it from the archive of the overview instead of creating a new one.',
  archive: {
    project_archived:
      "The project is archived: no new reports, time or planning are recorded on it. Restore it to book on it again.",
    work_time_model_archived:
      "The working-time model is archived: an existing assignment can only be ended, not extended, and it cannot be newly assigned or made the default.",
    user_archived: "The person has left. Reactivate them first.",
    user_archive_self: "You cannot mark yourself as having left.",
    user_archive_last_administrator: "The last active administrator cannot be marked as having left.",
  },
  csv: {
    empty_file: "The CSV file is empty.",
    parse_error: "The CSV file could not be read. Please check the format.",
    missing_required_column: "Required column(s) missing: {value}.",
    unknown_entity: "CSV import is not available for this data type.",
  },
  dataexport: {
    already_pending: "A data export is already being built. Please wait until it has finished.",
    not_available: "This archive is not (or no longer) available for download.",
  },
  billing: {
    interval_unavailable: "This billing interval is currently unavailable.",
    addon_unavailable: "Not available to book yet: {features}.",
    feature_not_entitled: "This feature is not included in your plan.",
  },
  file: {
    type_not_allowed: "“{name}” has a file type that is not accepted.",
    storage_limit_reached: "Your workspace has run out of storage.",
    too_large: "This file is too large. The maximum is {size}.",
  },
  request: {
    malformed: "The changes could not be saved. The error has been reported automatically.",
    invalid_field: "The changes could not be saved. Please check your input.",
  },
  category: {
    being_referenced_on_deletion: "Category can't be deleted because [[hwr:a/an]] [[hwr]] still uses it. Archive it instead.",
  },
  material: {
    being_referenced_on_deletion: "Material can't be deleted because it is already in use. Archive it instead.",
  },
  purchase_price_variant: {
    label_duplicate: "Two purchase prices have the same name. Please name them uniquely.",
    limit_exceeded: "At most 20 purchase prices per machine or material are possible.",
    report_variant_unknown:
      "The chosen purchase price no longer exists in the project. Please reload the form and pick the price again.",
  },
  machine: {
    being_referenced_on_deletion: "Machine can't be deleted because it is already in use. Archive it instead.",
    hourly_wage_report_unit_unresolved:
      "The machine has no billing unit yet. Please pick hour or day in the master data first.",
    hourly_wage_report_duration_not_in_hours: "New or changed machine usage is recorded in hours.",
    unit_not_allowed: "Machines are billed per hour or per day only.",
    selling_unit_requires_fixed_price:
      "A different selling unit requires a fixed price. A surcharge on an hourly price is not a daily price.",
  },
  hourlywagereport: {
    construction_report_required:
      "This project has no [[cr]] yet. Without one to carry the costs the [[hwr]] cannot be attached. Please create the [[cr]] first, or clear the option.",
  },
  additionaleffort: {
    being_referenced_on_deletion: "Additional-Effort can't be deleted because it is already in use. Archive it instead.",
  },
  user: {
    being_referenced_on_deletion: "User can't be deleted because it is already in use.",
    already_login_user: "This employee already has an account.",
    email_already_in_use: "This email address is already in use.",
  },
  userposition: {
    being_referenced_on_deletion: "User-Position can't be deleted because it is already in use. Archive it instead.",
  },
  constructionreport: {
    project_and_date_unique: "[[CR]] Date needs to be unique for the Project.",
    at_least_one_user_or_machine: "[[CR]] needs to have at least one User or Machine.",
    machine_hours_outside_shift: "A device's hours must be greater than 0 and fit into the shift.",
    breaks_outside_shift: "A shift's breaks must lie between its 'from' and 'to' times and together fit into the shift.",
    at_least_one_service_or_comment_or_occurrence: "[[CR]] needs at least one comment, service or special occurrence.",
  },
  project: {
    being_referenced_on_deletion: "The project can't be deleted because something was already recorded against it.",
  },
  // What a refused delete names as still holding the record (backend `UsageKind`), see `usedIn`.
  usage: {
    in: "Found: {list}.",
    archive_instead: "Archive the entry instead.",
    leave_instead: "Mark the person as having left instead.",
    PROJECT: "projects",
    CONSTRUCTION_REPORT: "[[cr.pl]]",
    HOURLY_WAGE_REPORT: "[[hwr.pl]]",
    HOURLY_WAGE_INVOICE: "[[hwi.pl]]",
    TIME_ENTRY: "time entries",
    DAY_SUBMISSION: "day submissions",
    SERVICE_PROGRESS: "service progress",
    ABSENCE: "absences",
    PERSONNEL_ASSIGNMENT: "personnel planning",
    VOICE_RECORDING: "voice recordings",
    EMPLOYEE: "employees",
    PURCHASE_PRICE_REVISION: "dated prices",
    OBJECT_PRICE: "project prices",
    RENTAL: "rentals",
  },
  // Rentals (docs/suppliers.md): violations.js appends the other rental's days.
  rental: {
    overlaps: "The device is already rented during that time.",
    overlaps_with: "The device is already rented during that time:",
    since: "since",
    not_picked_up: "not picked up yet",
    // A rental's accessory the server refuses: not (or no longer) the named supplier's, or picked twice.
    accessory_unknown: "The supplier has no such accessory of the rental. Please pick it again.",
    accessory_quantity: "An accessory's quantity must be above 0 and at most 9,999.",
    accessories_limit: "At most 20 accessories per rental.",
    // A rental's machine or device is not on the saved project.
    machine: "The machine is not on the saved project.",
    device: "The device is not on the saved project.",
    time_units: "Enter the duration for a transport by time.",
    // The device goes in front: "Minibagger 3 t #MB-03 is still rented: …"
    still_rented: "is still rented: remove the rental first.",
    still_rented_unnamed: "The machine is still rented: remove the rental first.",
    // A project with a running rental is not archived.
    running_on_archive: "A rental of the project is still running. End it first (“End rental”), then archive.",
    // The rental goes in front: "Minibagger 3 t #MB-03 since 10/12/2026 is still rented. …"; after it, when several run.
    running_named: "is still rented. End it first (“End rental”), then archive.",
    running_more: "Further running rentals: {count}.",
    // "Own company" does not rent from itself.
    own_company: "There is no renting from “Own company”. Pick the rental company.",
  },
  supplier: {
    // A supplier's accessories (docs/suppliers.md, "Zubehör").
    accessory_not_allowed: "“Own company” keeps no accessories.",
    accessory_last_price: "The only price stays. Remove the accessory instead.",
    accessory_duplicate: "The supplier already keeps an accessory of this name for this machine.",
    // The supplier's row on the machine has been removed meanwhile.
    accessory_no_row: "The supplier has no price on this machine any more. Please reload the page.",
    accessories_limit: "At most 200 accessories per supplier and machine.",
    being_referenced_on_deletion: "Supplier can't be deleted because it is already in use. Archive it instead.",
    built_in: "“Own company” stands for your own stock and can be neither archived nor deleted.",
    kind_being_referenced_on_deletion: "The kind can't be deleted because suppliers use it.",
    archived: "The supplier is archived: its prices and accessories stay as they are. Restore it first.",
    setup_required: "Supplier management is not set up yet.",
    setup_running: "The setup is already running. Please wait a moment and reload the page.",
    price_list_unreadable: "The price list cannot be read.",
    price_list_expired: "The uploaded price list has expired. Please upload it again.",
    price_list_applying: "This price list is being applied right now. Please wait a moment.",
    price_list_upload_limit:
      "The workspace already holds {max} price lists read in the last two hours. Please upload again later.",
    prices_at_supplier:
      "Purchase prices are maintained at the supplier. The page now shows purchasing with the suppliers.",
    setup_unmapped_label: "Every name used so far needs a supplier.",
    row_supplier_required: "Every purchase price needs a supplier.",
    row_duplicate:
      "This supplier already has a price here. For a second price from the same supplier, add a qualifier, e.g. “Night”.",
    revision_last: "A supplier's only price can't be deleted. Remove the supplier instead.",
    row_has_object_prices:
      "Projects have project prices on this price. Remove them there first, then the price can be deleted.",
    row_has_rentals:
      "Project rentals run on this price. Pick another row for them there first, then the price can be deleted.",
  },
  customer: {
    being_referenced_on_deletion: "Customer can't be deleted because it is already in use. Archive it instead.",
    contact: {
      position: {
        being_referenced_on_deletion: "Customer Contact Position can't be deleted because it is already in use.",
      },
      delete_has_reference: 'Customer Contact "{value}" can\'t be deleted because a reference of it is in use.',
    },
  },
  hourlywageinvoice: {
    missing_selling_price_for_user_position: 'Selling Price of Employee-Position "{value}" is unset.',
    missing_user_position_in_project: 'User Position "{value}" does not exist in the project.',
    missing_price_for_additional_effort:
      'Real Price must be set for additional effort "{value}", so the variable price can be calculated.',
    missing_purchase_price_for_material:
      'Purchase Price must be set for material "{value}", so the variable price can be calculated.',
    missing_purchase_price_for_machine:
      'Purchase Price must be set for machine "{value}", so the variable price can be calculated.',
    // The project row has no selling price at all: an empty fixed price, or a surcharge without its percentage.
    missing_selling_price_for_material: 'Material "{value}" has no selling price in this project.',
    missing_selling_price_for_machine: 'Machine "{value}" has no selling price in this project.',
    // No surcharge possible: the booked row bills per hour while the machine sells per day (or the other way round).
    machine_unit_needs_fixed_price:
      'Machine "{value}" needs a fixed price in this project: purchase and sale are in different units.',
    missing_selling_price_for_additional_effort: 'Additional effort "{value}" has no selling price in this project.',
    selling_price_already_set:
      "A selling price has been set in the project in the meantime. Your entry was not applied.",
    custom_number_too_long: "Your own invoice number can be at most {max} characters long.",
  },
  settings: {
    role: {
      edit_administrator: "Administrator Role can't be edited.",
      delete_administrator: "Administrator Role can't be deleted.",
      delete_used_reference: "Role can't be deleted while it's used by users.",
      administrator_only_permission: "Only the Administrator role can hold these permissions.",
      missing_permission_dependency: "Some of these permissions require others the role doesn't hold.",
    },
    logo: {
      unsupported_type: "The company logo must be a PNG or JPEG image.",
      too_large: "The company logo must not be larger than 2 MB.",
    },
  },
  // PDF templates: letterheads and templates.
  pdf: {
    letterhead_name_taken: "A letterhead with this name already exists.",
    template_name_taken: "This document already has a template with this name.",
    template_not_found: "The chosen template no longer exists.",
    type_mismatch: "The template belongs to another document.",
    letterhead_in_use: "The letterhead is still used by: {templates}.",
    image_in_use: "The picture is still placed on the letterhead. Remove its block first.",
    image_limit: "A letterhead holds at most {limit} pictures.",
    image_type: "A picture must be a PNG or JPEG.",
    image_too_large: "A picture must not be larger than 2 MB.",
    image_too_many_pixels: "A picture may have at most {megapixels} megapixels and {side} pixels a side.",
    system_read_only: "Built-in letterheads and templates can only be copied.",
    invalid: {
      BODY_TOO_SMALL: "The content area is too small. It needs at least 141 × 106 mm.",
      OFF_PAGE: "A block lies outside the page.",
      DUPLICATE_ID: "Two blocks have the same id.",
      UNKNOWN_PLACEHOLDER: "Unknown field in the text: {detail}",
      PAGE_TOTAL_MISPLACED: "The total page count can only end a page number.",
      IMAGE_MISSING: "A picture block has no picture.",
      LETTERHEAD_NOT_FOUND: "The chosen letterhead no longer exists.",
      TOO_MANY_LABELS: "At most three signatures.",
      LABEL_TOO_LONG: "A signature label is too long.",
      NULL_ELEMENT: "The design is incomplete. Please reload the page.",
      other: "This design cannot be printed.",
    },
  },
  daysubmission: {
    date_invalid: "The day is outside the days a report can be for.",
    too_large: "The day submission has more entries than a day can have.",
    comment_invalid: "The comment is empty or too long.",
    comment_limit: "The day submission has as many comments as it can take.",
    not_open: "The day submission is no longer open: it was taken over, is being taken over or was dismissed.",
    changed: "Something has happened to the day submission meanwhile. Please reload it.",
    report_busy: "Another report of this site and day is being saved right now. Try again in a moment.",
    entry_invalid: "This no longer fits the day submission. Open the report from the day submission again.",
    project_missing: "Choose a site for the report.",
    report_changed: "The day's [[cr]] has been changed meanwhile. Open the report from the day submission again.",
  },
  timetracking: {
    model_not_found: "The selected working-time model no longer exists. Please choose another.",
    model_being_referenced_on_deletion:
      "The working-time model can't be deleted because time was recorded under it or employees use it.",
    model_name_already_exists: "A working-time model with this name already exists.",
    model_name_archived: "An archived working-time model has this name. Restore it from the archive instead of creating a new one.",
    assignment_invalid: "The assignments overlap or end before they start. Please check the periods.",
    entry_not_found: "One of the selected entries no longer exists. Please reload the view.",
    invalid_time_range: "Start and end do not add up to any working time.",
    break_outside_shift: "A break lies outside the recorded working time.",
    breaks_overlap: "Two breaks overlap.",
    overlaps_existing: "An overlapping entry is already recorded for this employee on that day.",
    exceeds_daily_maximum: "The hours exceed the maximum the working-time model allows.",
    already_running: "A time entry is already running. Please stop it first.",
    not_running: "No time entry is currently running.",
    break_already_running: "A break is already running.",
    break_not_running: "No break is running for this day.",
    already_approved: "This entry is approved and must be reopened before it can be changed.",
    employee_not_found: "The employee could not be found.",
    absence_not_found: "This kind of absence no longer exists. Please pick another one.",
    absence_overlaps:
      "The absence does not fit beside the existing one on some days. Please enter it again and choose what happens to those days.",
    project_not_bookable: "You may not book time to this project. Please choose another one or none.",
    self_review: "Time entries you recorded yourself must be approved, rejected or reopened by someone else.",
    location_invalid: "The submitted location is invalid.",
  },
  personnelplanning: {
    date_exceeds_project_span: "Start date and end date must be within the project/phase date range",
    not_found: "Calendar could not be found.",
    name_already_exists: "Name already exists.",
    assignment_invalid_date_range: "Start- or End date of the assinment are invalid.",
    assignment_date_outside_project_range: "Start- and End date have to be inside the project's date range.",
    assignment_date_outside_phase_range: "Start- and End date have to be inside the phase's date range.",
    referenced_project_not_found: "Project could not be found.",
    referenced_employee_not_found: "Employee could not be found.",
    referenced_team_not_found: "Team could not be found.",
    referenced_phase_not_found: "Phase could not be found.",
    assignment_project_not_in_list: "Project has to be assigned to the calendar first.",
    assignment_employee_not_in_list: "Employee has to be assigned to the calendar first.",
    assignment_phase_not_found: "Phase could not be found.",
    includes_all_projects: "This calendar shows all projects. Projects cannot be added to or removed from it.",
    employee_on_project: "This person is on one of the projects and therefore always on the “All projects” calendar.",
    built_in: "The “All projects” calendar is built in and cannot be deleted, renamed or pinned.",
  },
  serviceCatalog: {
    position_identifier_not_unique: "This position number is already used in the service catalogue.",
    position_booked: "The position is booked on [[cr.pl]] and cannot be deleted or turned into a title.",
    title_not_empty: "There are still entries beneath this title.",
    invalid_parent: "The entry cannot be moved there.",
    change_order_number_not_unique: "This change order number is already used.",
    change_order_has_positions: "Positions are still assigned to this change order.",
    entry_position_mismatch: "A booked position does not belong to this project.",
    entry_not_bookable: "Alternative positions and positions of rejected change orders cannot be booked.",
    entry_duplicate: "A position is booked more than once. Book it once with the day's quantity.",
    cost_split_invalid: "The cost shares must add up to 100 %.",
    position_has_progress: "Progress readings are recorded for this position. Delete them in its history first.",
    progress_date_in_future: "A progress reading cannot lie in the future.",
    progress_not_recordable:
      "No progress reading can be recorded for alternative positions or positions of rejected change orders.",
    progress_position_mismatch: "A position does not belong to this project.",
    progress_date_taken: "The position already has a progress reading on this day.",
    gaeb_unsupported: "The file is not a supported GAEB DA XML exchange (X83, X84 or X86).",
  },
  pin: {
    type_not_supported: "This entry cannot be pinned.",
    limit_reached: "You can pin at most {max} entries in this list. Unpin another one first.",
  },
}
